import { useCallback, useMemo } from 'react'

import {
  DateFilterByOperator,
  Event,
  EventRecurrence,
  EventRecurrenceOrderByEnum,
} from '@tribeplatform/gql-client/types'
import { EventRecurrenceFields } from '@tribeplatform/gql-client/types/generated'
import { useEventRecurrences } from '@tribeplatform/react-sdk/hooks'

const DEFAULT_PAGE_SIZE = 25

interface UseBidirectionalEventRecurrencesOptions {
  event: Event
  fields?: EventRecurrenceFields
  limit?: number
}

interface UseBidirectionalEventRecurrencesResult {
  recurrences: EventRecurrence[]
  initialIndex: number
  isLoading: boolean
  isFetched: boolean
  hasPastRecurrences: boolean
  hasFutureRecurrences: boolean
  isFetchingPast: boolean
  isFetchingFuture: boolean
  fetchPastRecurrences: () => void
  fetchFutureRecurrences: () => void
}

/**
 * Custom hook for bidirectional pagination of event recurrences.
 * Fetches recurrences in both directions (past and future) from the current event.
 */
export const useBidirectionalEventRecurrences = ({
  event,
  fields,
  limit = DEFAULT_PAGE_SIZE,
}: UseBidirectionalEventRecurrencesOptions): UseBidirectionalEventRecurrencesResult => {
  const { recurrenceId, startTime: currentEventStartTime } = event

  // Query for past recurrences (before the current event)
  const {
    data: pastData,
    fetchNextPage: fetchPastRecurrences,
    hasNextPage: hasPastRecurrences = false,
    isFetchingNextPage: isFetchingPast = false,
    isLoading: isLoadingPast,
    isFetched: isFetchedPast,
  } = useEventRecurrences({
    variables: {
      recurrenceId,
      orderBy: EventRecurrenceOrderByEnum.startTime,
      startTime: {
        operator: DateFilterByOperator.lt,
        value: currentEventStartTime,
      },
      limit,
      reverse: true,
    },
    fields,
    useInfiniteQueryOptions: {
      enabled: !!recurrenceId && !!currentEventStartTime,
      refetchOnWindowFocus: false,
    },
  })

  // Query for future recurrences (from current event onwards)
  const {
    data: futureData,
    fetchNextPage: fetchFutureRecurrences,
    hasNextPage: hasFutureRecurrences = false,
    isFetchingNextPage: isFetchingFuture = false,
    isLoading: isLoadingFuture,
    isFetched: isFetchedFuture,
  } = useEventRecurrences({
    variables: {
      recurrenceId,
      orderBy: EventRecurrenceOrderByEnum.startTime,
      startTime: {
        operator: DateFilterByOperator.gte,
        value: currentEventStartTime,
      },
      limit,
    },
    fields,
    useInfiniteQueryOptions: {
      enabled: !!recurrenceId && !!currentEventStartTime,
      refetchOnWindowFocus: false,
    },
  })

  const { recurrences, initialIndex } = useMemo(() => {
    const pastRecurrences = [...(pastData?.pages ?? [])]
      .flatMap(page => page.nodes ?? [])
      .reverse()

    const futureRecurrences = (futureData?.pages ?? []).flatMap(
      page => page.nodes ?? [],
    )
    const allRecurrences = [...pastRecurrences, ...futureRecurrences]
    const currentIndex = allRecurrences.findIndex(r => r.id === event.id)

    return {
      recurrences: allRecurrences,
      initialIndex: currentIndex >= 0 ? currentIndex : pastRecurrences.length,
    }
  }, [pastData?.pages, futureData?.pages, event.id])

  const handleFetchPast = useCallback(() => {
    if (hasPastRecurrences && !isFetchingPast) {
      fetchPastRecurrences()
    }
  }, [fetchPastRecurrences, hasPastRecurrences, isFetchingPast])

  const handleFetchFuture = useCallback(() => {
    if (hasFutureRecurrences && !isFetchingFuture) {
      fetchFutureRecurrences()
    }
  }, [fetchFutureRecurrences, hasFutureRecurrences, isFetchingFuture])

  return {
    recurrences,
    initialIndex,
    isLoading: isLoadingPast || isLoadingFuture,
    isFetched: isFetchedPast && isFetchedFuture,
    hasPastRecurrences,
    hasFutureRecurrences,
    isFetchingPast,
    isFetchingFuture,
    fetchPastRecurrences: handleFetchPast,
    fetchFutureRecurrences: handleFetchFuture,
  }
}
